// Shared RBAC matrix — mirrors backend ROLE_PERMISSIONS in server.py
export const ROLE_LABELS = {
  super_admin: "Super Admin",
  operations_manager: "Operations Manager",
  zonal_manager: "Zonal Manager",
  field_executive: "Field Executive",
  channel_partner: "Channel Partner",
  farmer: "Farmer",
};

export const STAFF_ROLES = [
  "super_admin",
  "operations_manager",
  "zonal_manager",
  "field_executive",
  "channel_partner",
];

// Roles a Super Admin can assign in the Manage Team panel
export const ASSIGNABLE_ROLES = [...STAFF_ROLES, "farmer"];

export const normRole = (r) => (r === "admin" ? "super_admin" : r || "farmer");
export const isStaff = (r) => STAFF_ROLES.includes(normRole(r));

const LEVELS = { none: 0, view: 1, manage: 2, full: 3 };

// Dashboard modules. `built: false` renders a "Coming soon" tile (still permission-gated).
export const MODULES = [
  { key: "applications", label: "Applications", icon: "FileText", path: "/admin/applications", built: true, desc: "Review & track subsidy applications" },
  { key: "customer_data", label: "Customer / Farmer Data", icon: "Users", path: "/admin/applications", built: true, desc: "Applicant records & details" },
  { key: "documents", label: "Documents", icon: "FolderOpen", path: "/admin/applications", built: true, desc: "Uploaded application documents" },
  { key: "reports", label: "Reports", icon: "BarChart3", path: "/admin/applications", built: true, desc: "Stats & performance reports" },
  { key: "user_management", label: "User Management", icon: "ShieldCheck", path: "/admin?team=1", built: true, desc: "Team members, roles & access" },
  { key: "staff_management", label: "Staff Management", icon: "UserCog", path: "/admin/staff", built: true, desc: "Manage staff across zones" },
  { key: "zone_management", label: "Zone Management", icon: "Map", path: "/admin/zones", built: true, desc: "Define & assign operational zones" },
  { key: "task_assignment", label: "Task Assignment", icon: "ClipboardList", path: "/admin/tasks", built: true, desc: "Assign & track field tasks" },
  { key: "payments", label: "Payments & Commission", icon: "IndianRupee", built: false, desc: "Disbursements & partner commission" },
  { key: "system_settings", label: "System Settings", icon: "Settings", path: "/admin/settings", built: true, desc: "Global configuration" },
];

export const ROLE_PERMISSIONS = {
  super_admin: {
    dashboard: "full", staff_management: "full", zone_management: "full", customer_data: "full",
    applications: "full", documents: "full", task_assignment: "full", payments: "full",
    user_management: "full", reports: "full", system_settings: "full",
  },
  operations_manager: {
    dashboard: "view", staff_management: "manage", zone_management: "manage", customer_data: "manage",
    applications: "manage", documents: "manage", task_assignment: "manage", payments: "manage",
    user_management: "manage", reports: "view", system_settings: "none",
  },
  zonal_manager: {
    dashboard: "view", staff_management: "view", zone_management: "manage", customer_data: "manage",
    applications: "manage", documents: "manage", task_assignment: "manage", payments: "view",
    user_management: "none", reports: "view", system_settings: "none",
  },
  field_executive: {
    dashboard: "view", staff_management: "none", zone_management: "none", customer_data: "manage",
    applications: "manage", documents: "manage", task_assignment: "none", payments: "none",
    user_management: "none", reports: "view", system_settings: "none",
  },
  channel_partner: {
    dashboard: "view", staff_management: "none", zone_management: "none", customer_data: "manage",
    applications: "manage", documents: "manage", task_assignment: "none", payments: "view",
    user_management: "none", reports: "view", system_settings: "none",
  },
  farmer: {
    dashboard: "view", applications: "manage", documents: "manage",
  },
};

// Scope descriptor shown on the dashboard header per role
export const ROLE_SCOPE = {
  super_admin: "Full system access",
  operations_manager: "All zones & staff",
  zonal_manager: "Assigned zone only",
  field_executive: "Assigned customers only",
  channel_partner: "Own leads & commission",
  farmer: "Own applications",
};

export const permLevel = (role, module) => ROLE_PERMISSIONS[normRole(role)]?.[module] || "none";
export const can = (role, module, level = "view") => LEVELS[permLevel(role, module)] >= LEVELS[level];

// Roles allowed to change application status / edit with audit trail
export const REVIEW_ROLES = ["super_admin", "operations_manager", "zonal_manager"];
export const canReview = (role) => REVIEW_ROLES.includes(normRole(role));
